import { AsyncPipe, DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MarkdownPipe, MarkdownService as NgxMarkdownService } from 'ngx-markdown';

import { ExportFilesFactory } from '@shared/services/file-export/drivers';
import { TFileExportDriversAvailable } from '@shared/services/file-export/file-export-interface';

import { ConversationDataService } from '../../../../services/conversation-data.service';
import { FileExportService } from '@shared/services/file-export/services/file-export.service';
import { ReferencesUtils } from '@shared/utils/referencesUtils';
import { stripUnicodeIcons } from '@shared/utils/strip-unicode-icons.helper';
@Component({
  selector: 'app-select-items-to-export-modal',
  templateUrl: './select-items-to-export-modal.component.html',
  styleUrls: ['./select-items-to-export-modal.component.scss'],
  standalone: true,
  imports: [
    MatButton,
    MatButtonToggleModule,
    MatIcon,
    MatCard,
    MatCardContent,
    MatCheckbox,
    ReactiveFormsModule,
    FormsModule,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    DatePipe,
    MarkdownPipe,
    AsyncPipe,
  ],
})
export class SelectItemsToExportModalComponent implements OnInit {
  protected readonly ExportFilesFactory = ExportFilesFactory;
  protected selectedAll = false;
  protected selectedItems: { [id: number]: boolean } = {};
  protected includeRefs = true;
  protected preserveFormatting = true;
  protected exportMode: 'messages' | 'tables' = 'messages';
  protected selectedTablesAll = false;
  protected selectedTables: { [id: string]: boolean } = {};
  protected tableItems: TableExportItem[] = [];

  constructor(
    protected conversationService: ConversationDataService,
    private dialogRef: MatDialogRef<SelectItemsToExportModalComponent>,
    private fileExportService: FileExportService,
    private ngxMarkdownService: NgxMarkdownService
  ) {
    this.dialogRef.afterClosed().subscribe(() => {
      this.selectedAll = true;
      this.toggleSelectAll();
    });
  }

  ngOnInit(): void {
    this.refreshTablesFromConversation();
  }

  cancel() {
    this.dialogRef.close();
  }

  toggleSelection(index: number, ev: MatCheckboxChange) {
    this.selectedItems[index] = ev.checked;
    this.updateToggleAll();
  }

  protected updateToggleAll() {
    this.selectedAll =
      this.countSelectedItems() === this.conversationService.currentValue?.messages.length;
  }

  exportAs(driver: TFileExportDriversAvailable) {
    if (this.exportMode === 'tables') {
      this.exportTables();
      return;
    }

    const selectedMessages =
      this.conversationService.currentValue?.messages.filter(
        (item, index) => this.selectedItems[index]
      ) || [];

    // For pdf-new (ImageToPdfDriver), extract rendered HTML from preview
    if (driver === 'pdf-new') {
      const renderedHtmlContent = stripUnicodeIcons(this.extractRenderedHtml(selectedMessages));

      const exportConfig = {
        driver,
        pages: [renderedHtmlContent], // Pass rendered HTML instead of raw messages
        isHtml: true, // Flag to indicate HTML content
        options: {
          pageNumber: true,
          headerLogo: true,
          confidentialityClause: true,
          confidentialityClauseText: 'This document is confidential',
        },
      };

      this.fileExportService.getFile(exportConfig);
      this.cancel();
      return;
    }

    // For html2pdf driver, pass messages directly for better formatting
    if (driver === 'html2pdf') {
      const exportConfig = {
        driver,
        messages: this.stripIconsFromMessages(selectedMessages), // Pass messages directly instead of concatenated text
        options: {
          pageNumber: true,
          headerLogo: true,
          confidentialityClause: true,
          confidentialityClauseText: 'This document is confidential',
        },
      };

      this.fileExportService.getFile(exportConfig);
      this.cancel();
      return;
    }

    // For other drivers, use the existing logic
    let exportContent: string;

    if (driver === 'pdf' && this.preserveFormatting) {
      // For PDF with formatting, use the smart Mermaid + references logic
      // Pass the original message objects to preserve paragraph structure
      const exportConfig = {
        driver,
        messages: this.stripIconsFromMessages(selectedMessages), // Pass original message objects instead of processed string
        isHtml: false, // Let the PDF driver handle the processing
        options: {
          pageNumber: true,
          headerLogo: true,
          confidentialityClause: true,
          confidentialityClauseText: 'This document is confidential',
        },
      };

      this.fileExportService.getFile(exportConfig);
      this.cancel();
      return; // Exit early since we're using a different approach
    } else {
      // For other formats or plain text, use existing method
      exportContent = stripUnicodeIcons(
        selectedMessages
          .map(message =>
            this.conversationService.currentValue!.getMessageToString(message, this.includeRefs)
          )
          .join('')
      );
    }

    const exportConfig = {
      driver,
      pages: [exportContent],
      isHtml: driver === 'pdf' && this.preserveFormatting, // Flag to indicate HTML content
      options: {
        pageNumber: true,
        headerLogo: true,
        confidentialityClause: true,
        confidentialityClauseText: 'This document is confidential',
      },
    };

    this.fileExportService.getFile(exportConfig);
    this.cancel();
  }

  countSelectedItems(): number {
    return Object.values(this.selectedItems).filter(value => value).length;
  }

  isValid(): boolean {
    if (this.exportMode === 'tables') {
      return this.countSelectedTables() > 0;
    }

    return this.countSelectedItems() > 0;
  }

  toggleSelectAll() {
    this.selectedAll = !this.selectedAll;
    this.conversationService.currentValue?.messages.map(
      (value, index) => (this.selectedItems[index] = this.selectedAll)
    );
  }

  toggleSelectAllTables() {
    this.selectedTablesAll = !this.selectedTablesAll;
    this.tableItems.forEach(item => (this.selectedTables[item.id] = this.selectedTablesAll));
  }

  getMessagePreview(message: any): string {
    const text = message.paragraphs[0]?.text || '';
    return text.length > 100 ? text.substring(0, 100) + '...' : text;
  }

  getSelectedMessages(): any[] {
    const selectedMessages =
      this.conversationService.currentValue?.messages.filter(
        (item, index) => this.selectedItems[index]
      ) || [];

    return selectedMessages;
  }

  getSelectedTables(): TableExportItem[] {
    return this.tableItems.filter(item => this.selectedTables[item.id]);
  }

  getFormattedMessage(message: any): string {
    if (this.preserveFormatting) {
      // For preview, get raw markdown text (preserve formatting)
      const formattedParagraphs = message.paragraphs
        .map((paragraph: any) => {
          let text = paragraph.text.trim();
          if (this.includeRefs && paragraph.references?.length) {
            const refs = paragraph.references
              .map((ref: any) => `[${ref.listPositionIndex}]`)
              .join(' ');
            text = `${text} ${refs}`;
          }
          return text;
        })
        .filter((text: string) => text.trim()); // Remove empty paragraphs

      // Join paragraphs with proper spacing, but be careful with Mermaid blocks
      const joinedContent = formattedParagraphs.join('\n\n');

      // Validate and fix common Mermaid syntax issues
      return this.validateAndFixMermaidContent(joinedContent);
    } else {
      // For plain text, use existing sanitized method
      return this.conversationService.currentValue!.getMessageToString(message, this.includeRefs);
    }
  }

  private validateAndFixMermaidContent(content: string): string {
    // Fix common Mermaid syntax issues that could cause rendering errors
    let fixedContent = content;

    // Ensure Mermaid code blocks are properly formatted
    // Fix: ```mermaid\n -> ```mermaid\n
    fixedContent = fixedContent.replace(/```mermaid\s*\n/g, '```mermaid\n');

    // Fix: Ensure proper closing of Mermaid blocks
    fixedContent = fixedContent.replace(/```\s*\n\s*```/g, '```\n```');

    // Remove any malformed Mermaid blocks that might cause syntax errors
    // This regex finds incomplete Mermaid blocks and removes them
    fixedContent = fixedContent.replace(/```mermaid\s*\n[^`]*?(?=\n```|$)/g, match => {
      // If the block doesn't end properly, remove it
      if (!match.includes('```')) {
        return '';
      }
      return match;
    });

    return fixedContent;
  }

  getParagraphText(paragraph: any): string {
    let text = paragraph.text.trim();

    if (this.includeRefs && paragraph.references?.length) {
      const refs = paragraph.references.map((ref: any) => `[${ref.listPositionIndex}]`).join(' ');
      text = `${text} ${refs}`;
    }

    return text;
  }

  private checkMermaidSyntax(mermaidCode: string): string[] {
    const issues: string[] = [];

    // Check for common Mermaid syntax issues
    if (mermaidCode.includes('```')) {
      issues.push('Contains nested code blocks');
    }

    if (mermaidCode.includes('\\n') && !mermaidCode.includes('\n')) {
      issues.push('Contains escaped newlines instead of actual newlines');
    }

    if (mermaidCode.includes('&lt;') || mermaidCode.includes('&gt;')) {
      issues.push('Contains HTML entities instead of actual characters');
    }

    // Check for unclosed quotes or brackets
    const quoteCount = (mermaidCode.match(/"/g) || []).length;
    if (quoteCount % 2 !== 0) {
      issues.push('Unmatched quotes');
    }

    return issues;
  }

  toggleMessageSelection(index: number): void {
    this.selectedItems[index] = !this.selectedItems[index];
    this.updateToggleAll();
  }

  toggleTableSelection(id: string): void {
    this.selectedTables[id] = !this.selectedTables[id];
    this.updateToggleAllTables();
  }

  toggleTableSelectionByCheckbox(id: string, ev: MatCheckboxChange) {
    this.selectedTables[id] = ev.checked;
    this.updateToggleAllTables();
  }

  extractRenderedHtml(selectedMessages: any[]): string {
    const htmlParts: string[] = [];

    selectedMessages.forEach((message, index) => {
      // Add message header
      const authorName = message.author === 'user' ? 'You' : this.getBotName();
      htmlParts.push(`<h2>${authorName}</h2>`);

      // Process each paragraph individually (EXACTLY like the working preview)
      message.paragraphs.forEach((paragraph: any) => {
        // Use EXACTLY the same approach as the working preview - NO TRIMMING!
        let paragraphText = paragraph.text; // No .trim() - preserve original whitespace

        // Check if this paragraph contains Mermaid
        const hasMermaid = paragraphText.includes('```mermaid');

        // Only add references if needed, but handle Mermaid paragraphs differently
        if (this.includeRefs && paragraph.references?.length) {
          const refs = paragraph.references
            .map((ref: any) => `[${ref.listPositionIndex}]`)
            .join(' ');

          if (hasMermaid) {
            // For Mermaid paragraphs, add references AFTER the Mermaid block
            // Find the end of the Mermaid block and insert references there
            const mermaidEndIndex = paragraphText.lastIndexOf('```');
            if (mermaidEndIndex !== -1) {
              const beforeMermaid = paragraphText.substring(0, mermaidEndIndex + 3);
              const afterMermaid = paragraphText.substring(mermaidEndIndex + 3);
              // Add a proper line break to separate Mermaid from references
              paragraphText = `${beforeMermaid}${afterMermaid}\n\n${refs}`;
            } else {
              // Fallback: add at the end
              paragraphText = `${paragraphText} ${refs}`;
            }
          } else {
            // For non-Mermaid paragraphs, add references normally
            paragraphText = `${paragraphText} ${refs}`;
          }
        }

        // Use the same markdown service as the preview
        const renderedParagraphHtml = this.ngxMarkdownService.parse(paragraphText, {
          mermaid: true,
          disableSanitizer: true,
        });
        htmlParts.push(renderedParagraphHtml as string); // TODO check if it works
      });

      // Add references list at the end of the message (like getMessageToString does)
      if (this.includeRefs && message.referencesToDisplay?.length) {
        const referenceLabel = this.getReferenceLabel();
        let referencesHtml = `<div class="references-section" style="margin-top: 20px; padding-top: 15px; border-top: 1px solid #ccc;">`;
        referencesHtml += `<h3 style="margin: 0 0 10px 0; font-size: 14pt; font-weight: bold;">${referenceLabel}:</h3>`;

        message.referencesToDisplay.forEach((ref: any) => {
          const displayName = ref.originalSource?.citation || ref.originalSource?.name;
          if (ref.children?.length && displayName) {
            const referenceNumbers = ref.children
              .map((child: any) => `[${child.listPositionIndex}]`)
              .join(' ');
            referencesHtml += `<p style="margin: 5px 0; font-size: 11pt;">${referenceNumbers} ${displayName}</p>`;
          }
        });

        referencesHtml += '</div>';
        htmlParts.push(referencesHtml);
      }

      // Add separator between messages (but not between paragraphs)
      if (index < selectedMessages.length - 1) {
        htmlParts.push('<hr>');
      }
    });

    const fullHtml = htmlParts.join(''); // No extra newlines between paragraphs

    return fullHtml;
  }

  countSelectedTables(): number {
    return Object.values(this.selectedTables).filter(value => value).length;
  }

  private updateToggleAllTables() {
    this.selectedTablesAll =
      this.tableItems.length > 0 && this.countSelectedTables() === this.tableItems.length;
  }

  onExportModeChange(): void {
    if (this.exportMode === 'tables') {
      this.refreshTablesFromConversation();
    }
  }

  private refreshTablesFromConversation(): void {
    const messages = this.conversationService.currentValue?.messages || [];
    const tables: TableExportItem[] = [];
    let tableIndex = 0;

    messages.forEach((message, messageIndex) => {
      const htmlContent = message.paragraphs
        .map((paragraph: any) =>
          this.ngxMarkdownService.parse(paragraph.text, {
            mermaid: true,
            disableSanitizer: true,
          })
        )
        .join('');

      if (!htmlContent) {
        return;
      }

      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent as string, 'text/html');
      const messageTables = Array.from(doc.querySelectorAll('table'));

      messageTables.forEach(table => {
        const extracted = this.extractTableData(table);
        if (!extracted) {
          return;
        }

        tableIndex += 1;
        const id = `${messageIndex}-${tableIndex}`;
        tables.push({
          id,
          sourceMessageIndex: messageIndex,
          label: this.buildTableLabel(extracted.headers, messageIndex, tableIndex),
          headers: extracted.headers,
          rows: extracted.rows,
          rowCount: extracted.rows.length,
        });
      });
    });

    this.tableItems = tables;
    this.selectedTables = {};
    this.selectedTablesAll = false;
  }

  private extractTableData(table: HTMLTableElement): ExtractedTableData | null {
    const rows = Array.from(table.querySelectorAll('tr')) as HTMLTableRowElement[];
    if (!rows.length) {
      return null;
    }

    const headerRow = (table.querySelector('thead tr') as HTMLTableRowElement | null) || rows[0];
    const headerCells = headerRow ? Array.from(headerRow.querySelectorAll('th')) : [];
    let headers: string[] = [];
    let startRowIndex = 0;

    if (headerCells.length > 0) {
      headers = headerCells.map(cell => this.getCellText(cell));
      startRowIndex = rows.indexOf(headerRow) + 1;
    } else {
      const firstRowCells = Array.from(rows[0].querySelectorAll('td, th'));
      headers = firstRowCells.map(cell => this.getCellText(cell));
      startRowIndex = 1;
    }

    const dataRows = rows
      .slice(startRowIndex)
      .map(row => Array.from(row.querySelectorAll('td, th')).map(cell => this.getCellText(cell)));

    const maxColumns = Math.max(headers.length, ...dataRows.map(row => row.length), 0);
    if (maxColumns > headers.length) {
      for (let i = headers.length; i < maxColumns; i += 1) {
        headers.push(`Column ${i + 1}`);
      }
    }

    return { headers, rows: dataRows };
  }

  private buildTableLabel(headers: string[], messageIndex: number, tableIndex: number): string {
    const headerLabel = headers.filter(Boolean).join(' | ');
    if (headerLabel) {
      return `Table ${tableIndex} (Message ${messageIndex + 1}): ${headerLabel}`;
    }
    return `Table ${tableIndex} (Message ${messageIndex + 1})`;
  }

  private getCellText(cell: Element): string {
    return (cell.textContent || '').trim();
  }

  exportTables(): void {
    const selectedTables = this.getSelectedTables();
    if (!selectedTables.length) {
      return;
    }

    const unionHeaders: string[] = [];
    const headerSet = new Set<string>();

    selectedTables.forEach(table => {
      table.headers.forEach(header => {
        const normalized = header.trim();
        if (!headerSet.has(normalized)) {
          headerSet.add(normalized);
          unionHeaders.push(normalized);
        }
      });
    });

    const csvRows: string[] = [];
    csvRows.push(unionHeaders.map(header => this.escapeCsvValue(header)).join(','));

    selectedTables.forEach(table => {
      table.rows.forEach(row => {
        const rowMap: Record<string, string> = {};
        table.headers.forEach((header, index) => {
          rowMap[header] = row[index] ?? '';
        });

        const rowValues = unionHeaders.map(header => this.escapeCsvValue(rowMap[header] || ''));
        csvRows.push(rowValues.join(','));
      });
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `tables-export-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    this.cancel();
  }

  /** Deep-clones the messages array and strips icons from every paragraph text. */
  private stripIconsFromMessages(messages: any[]): any[] {
    return messages.map(message => ({
      ...message,
      paragraphs: (message.paragraphs ?? []).map((paragraph: any) => ({
        ...paragraph,
        text: stripUnicodeIcons(paragraph.text ?? ''),
      })),
    }));
  }

  private escapeCsvValue(value: string): string {
    if (value.includes(',') || value.includes('"') || value.includes('\n')) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  }

  getReferenceLabel(): string {
    return ReferencesUtils.getReferenceLabelForLanguage(
      this.conversationService.currentValue?.language
    );
  }

  getBotName(): string {
    // Get bot name from conversation, fallback to 'Assistant'
    return this.conversationService.currentValue?.bot?.name || 'Assistant';
  }

  getDriverDisplayName(slug: TFileExportDriversAvailable): string {
    switch (slug) {
      case 'pdf-new':
        return 'PDF';
      case 'html2pdf':
        return 'HTML to PDF';
      default:
        return (slug as string).toUpperCase();
    }
  }
}

type ExtractedTableData = {
  headers: string[];
  rows: string[][];
};

type TableExportItem = ExtractedTableData & {
  id: string;
  label: string;
  sourceMessageIndex: number;
  rowCount: number;
};
